import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Input, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import { showError, showToast } from '../../constants/toasts';
import { Loader } from '../../helpers';
import MainContainer from '../../layout/MainContainer';
import {
  useAddBrandMutation,
  useLazyGetBrandByIdQuery,
  useUpdateBrandMutation,
} from '../../service/brands';
import { CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { UploadMedia } from '../../utils/mediaUpload';

const AddBrand = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [image, setImage] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [addBrand] = useAddBrandMutation();
  const [updateBrand] = useUpdateBrandMutation();
  const [getDetail] = useLazyGetBrandByIdQuery();

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setImage('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setImage(imageUrl?.data?.image || '');
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: '',
    },

    validationSchema: Yup.object({
      name: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      if (!image) {
        return;
      }

      const body = {
        image: image,
        name: values?.name || '',
      };

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateBrand({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Brand updated successfully');
            navigate('/vehicle-specifications/brands');
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          setLoading(false);

          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addBrand({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Brand added successfully');
            navigate('/vehicle-specifications/brands');
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          setLoading(false);

          console.error(error);
        }
      }
    },
  });

  const getBrand = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        formik.setFieldValue('name', response?.data?.name);
        setImage(response?.data?.image);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getBrand(id);
    }
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Brand</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/vehicle-specifications/brands')}>
              Vehicle Specifications
            </span>{' '}
            - <span onClick={() => navigate('/vehicle-specifications/brands')}>Brands</span> -{' '}
            {id ? 'Edit' : 'Add'}
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Brand Image</label>
              <div className="upload_box">
                <label>
                  {image && (
                    <IconButton className="crossIcon" onClick={() => setImage('')}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img src={image ? image : '/static/images/picture.png'} alt="Upload" />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file-profile">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file-profile"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg, image/svg',
                          }}
                          onChange={(e: any) => {
                            handleImageUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
                {error && !image ? <h6 className="err_msg">Image is required</h6> : null}
              </div>
            </div>
            <div className="control_group w_50" />
            {/* <div className="control_group w_50">
              <label>Select Vehicle Type</label>
              <Select
                fullWidth
                labelId="vehicleType"
                id="vehicleType"
                value={vehicleType}
                onChange={handleVehicleType}
              >
                <MenuItem disabled value="default">
                  Select Vehicle Type
                </MenuItem>
                <MenuItem value={VEHICLE_TYPE.CAR}>Car</MenuItem>
                <MenuItem value={VEHICLE_TYPE.BIKE}>Bike</MenuItem>
                <MenuItem value={VEHICLE_TYPE.TRUCK}>Truck</MenuItem>
                <MenuItem value={VEHICLE_TYPE.HEAVY_VEHICLE}>
                  Heavy Vehicle
                </MenuItem>
              </Select>
            </div> */}
            <div className="control_group w_50">
              <label>Brand Name</label>
              <TextField
                variant="outlined"
                placeholder="Brand Name"
                fullWidth
                hiddenLabel
                name="name"
                id="name"
                className="text_field"
                inputProps={{ maxLength: 30 }}
                value={formik.values.name}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
          </div>

          <div className="form_end_btn">
            <Button className="btn btn_secondary" type="submit" onClick={() => setError(true)}>
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddBrand;
